import { memo } from 'react';
import { Tooltip } from '@shared/components/Tooltip';
import { Chip } from './Chip';
import { ChipMoreLink } from './ChipMoreLink';
import type { ChipsWithMoreProps } from './types';

/**
 * Renders a row of chips plus "+N więcej" when there are more items than maxVisible.
 * Matches Figma Row Cell (Chips) layout: gap 8px, flex-start align.
 * Long chip labels are truncated with ellipsis; hover shows tooltip with full text when truncated.
 */
const PLAN_NAMES_TOOLTIP_MAX_HEIGHT = 320;

function ChipsWithMoreComponent({
  items,
  maxVisible = 1,
  moreLabel = 'więcej',
  tooltipMaxWidth = 480,
  tooltipMaxHeight = PLAN_NAMES_TOOLTIP_MAX_HEIGHT,
  className = '',
  emptyPlaceholder = '—',
}: ChipsWithMoreProps) {
  if (items.length === 0) {
    return (
      <span className="text-[#474A4C] text-sm leading-5">
        {emptyPlaceholder}
      </span>
    );
  }

  const visible = items.slice(0, maxVisible);
  const restCount = items.length - visible.length;
  const allPlanNames = items.map((i) => i.name).join(', ');
  const isSinglePlan = items.length === 1;

  return (
    <div className={`flex flex-nowrap items-center gap-2 min-w-0 ${className}`}>
      {visible.map((item) => (
        <span
          key={item.id}
          className="inline-flex min-w-0 shrink"
          style={
            isSinglePlan
              ? { width: '132px', maxWidth: '100%', minWidth: 0 }
              : restCount > 0
                ? { maxWidth: 'calc(100% - 80px)', minWidth: 0 }
                : undefined
          }
        >
          <Tooltip
            content={item.name}
            contentMaxWidth={tooltipMaxWidth}
            useContentWidth={item.name.length > 50}
          >
            <Chip>{item.name}</Chip>
          </Tooltip>
        </span>
      ))}
      {restCount > 0 && (
        <span className="shrink-0 flex-nowrap">
          <ChipMoreLink
            count={restCount}
            label={moreLabel}
            tooltipContent={allPlanNames}
            tooltipMaxWidth={tooltipMaxWidth}
            tooltipMaxHeight={tooltipMaxHeight}
          />
        </span>
      )}
    </div>
  );
}

export const ChipsWithMore = memo(ChipsWithMoreComponent);
